import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStoreon } from 'storeon/react';
import { useTranslation } from 'react-i18next';
import { APPLY_FAVORITE_ADDRESS, FETCH_FAVORITE_ADDRESS } from 'actions';
import { FAVORITE_DELIVERY_TYPE } from 'common/constants';
import { applyCheckoutInfoDeletions, getCheckoutInfo } from 'common/utils/checkout-info';
import { applyFavoriteAddressConfirm, getFavoriteDeliveryType } from 'common/utils/favorite-address';
import { State } from 'store/types';
import { FavoriteDeliveryType } from 'types/checkout-info';
import { DeletableFavoriteAddressType } from 'types/favorite-address';
import { HeaderModelData } from 'types/model';
import { getShortenRegionName } from 'utils';
import { useRegions } from '../../../hooks';
import { getSelectionGroupList } from '../get-selection-group-list';
import { AddressSheetItemType } from '../utils';
import { useAddressActionsMenu } from './use-address-actions-menu';
import { useFavoriteAddressDeliverySnack } from './use-favorite-address-delivery-snack';

type UseAddressMenuParams = {
  show: boolean;
  model: HeaderModelData;
  onMenuClose: () => void;
  onRegionsMenuOpen: () => void;
};

export const useAddressMenu = ({ show, model, onMenuClose, onRegionsMenuOpen }: UseAddressMenuParams) => {
  const { t } = useTranslation();
  const { dispatch, favoriteAddress } = useStoreon<State>('dispatch', 'favoriteAddress');
  const { defaultRegion, currentRegion } = useRegions(model);
  const [deliveryType, setDeliveryType] = useState<FavoriteDeliveryType>(FAVORITE_DELIVERY_TYPE.REGION);
  const [deletedAddressTypes, setDeletedAddressTypes] = useState<DeletableFavoriteAddressType[]>([]);
  const prevShow = useRef(false);

  const checkoutInfo = getCheckoutInfo();
  const regionName = getShortenRegionName(currentRegion?.regionName || defaultRegion.regionName);
  const selectionGroupList = useMemo(() => getSelectionGroupList(t), [t]);
  const deletedAddressTypesSet = useMemo(() => new Set(deletedAddressTypes), [deletedAddressTypes]);
  const showDeliverySnack = useFavoriteAddressDeliverySnack();

  const handleConfirm = useCallback(() => {
    const previousType = getFavoriteDeliveryType(favoriteAddress.data);

    const nextCheckoutInfo = applyCheckoutInfoDeletions(getCheckoutInfo(), deletedAddressTypes);

    dispatch(APPLY_FAVORITE_ADDRESS.confirm, { deliveryType, deletedAddressTypes });
    onMenuClose();

    const { state: nextState } = applyFavoriteAddressConfirm(
      favoriteAddress.data,
      { deliveryType, deletedAddressTypes },
      nextCheckoutInfo,
    );
    const effectiveType = getFavoriteDeliveryType(nextState);

    if (effectiveType !== previousType) {
      showDeliverySnack(effectiveType);
    }
  }, [deletedAddressTypes, deliveryType, dispatch, favoriteAddress.data, onMenuClose, showDeliverySnack]);

  const handleAddressActionSelect = useCallback(
    (action: 'edit' | 'delete', addressType: DeletableFavoriteAddressType) => {
      if (action !== 'delete') {
        return;
      }

      setDeletedAddressTypes((current) =>
        current.includes(addressType) ? current : [...current, addressType],
      );

      if (deliveryType === addressType) {
        setDeliveryType(FAVORITE_DELIVERY_TYPE.REGION);
      }
    },
    [deliveryType],
  );

  const {
    menuCoords,
    menuRef,
    isMenuOpen,
    handleAddressMenuOpen,
    handleAddressActionSelect: handleMenuSelect,
  } = useAddressActionsMenu({ isContainerOpen: show, onActionSelect: handleAddressActionSelect });

  const handleItemIconClick = useCallback(
    (itemId: AddressSheetItemType, element: HTMLElement) => {
      if (itemId === FAVORITE_DELIVERY_TYPE.REGION) {
        onRegionsMenuOpen();
        return;
      }

      handleAddressMenuOpen(itemId, element);
    },
    [handleAddressMenuOpen, onRegionsMenuOpen],
  );

  useEffect(() => {
    const open = show && !prevShow.current;

    if (open) {
      dispatch(FETCH_FAVORITE_ADDRESS.loadSheet);

      if (!favoriteAddress.loading) {
        setDeliveryType(getFavoriteDeliveryType(favoriteAddress.data));
        setDeletedAddressTypes([]);
      }
    }

    prevShow.current = show;
  }, [show, dispatch, favoriteAddress.loading, favoriteAddress.data]);

  return {
    t,
    favoriteAddress,
    deliveryType,
    setDeliveryType,
    selectionGroupList,
    deletedAddressTypesSet,
    regionName,
    checkoutInfo,
    handleConfirm,
    handleItemIconClick,
    isMenuOpen,
    menuCoords,
    menuRef,
    handleMenuSelect,
  };
};
